import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getStoredUser } from '@/core/token';
import { execute } from '@/graphql/execute';
import { MeUserSessionsQuery } from '@/graphql/graphql';
import { revokeSessionMutation } from '@/mutations/user.mutation';
import { meUserSessionsQuery } from '@/queries/me.query';
import { Desktop, MobileSolid, TabletSolid, Trash } from '@mynaui/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ChevronDown } from 'lucide-react';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import PortalTooltip from '../PortalTooltip';
import { Button } from '../ui/button';

dayjs.extend(relativeTime);
// To enable full ua-parser-js functionality:
// 1. Run: npm install ua-parser-js @types/ua-parser-js
// 2. Uncomment the import below
// 3. Replace the parseUserAgent function with the ua-parser-js version
// import { UAParser } from 'ua-parser-js';

interface LoginSessionsProps {
  userId: string;
}

export const LoginSessions: FunctionComponent<LoginSessionsProps> = ({ userId }) => {
  const [data, setData] = useState<MeUserSessionsQuery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingSession, setRevokingSession] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const result = await execute(meUserSessionsQuery, { userId });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const sessionId = useMemo(() => {
    const storedUser = getStoredUser();
    return storedUser?.sessionId;
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [userId]);

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setRevokingSession(sessionId);
      await execute(revokeSessionMutation, { sessionId });
      toast.success('Session revoked successfully');
      // Refresh the sessions list
      await fetchSessions();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const formatDate = (dateString: Date) => {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm');
  };

  const parseUserAgent = (userAgent: string) => {
    if (!userAgent) return null;

    // Simple fallback parsing without ua-parser-js
    // For full functionality, replace this with:
    // const parser = new UAParser(userAgent);
    // return parser.getResult();
    const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?=.*Mobile)/i.test(userAgent);
    const isWindows = /Windows/i.test(userAgent);
    const isMac = /Macintosh|Mac OS X/i.test(userAgent);
    const isLinux = /Linux/i.test(userAgent);

    // Extract browser info
    let browserName = 'Unknown Browser';
    let browserVersion = '';

    if (userAgent.includes('Chrome')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }

    // Extract OS info
    let osName = 'Unknown OS';
    let osVersion = '';

    if (isWindows) {
      osName = 'Windows';
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (match) {
        const version = match[1];
        if (version === '10.0') osVersion = '10';
        else if (version === '6.3') osVersion = '8.1';
        else if (version === '6.2') osVersion = '8';
        else if (version === '6.1') osVersion = '7';
      }
    } else if (isMac) {
      osName = 'macOS';
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      if (match) osVersion = match[1].replace('_', '.');
    } else if (isLinux) {
      osName = 'Linux';
    }

    return {
      browser: { name: browserName, version: browserVersion },
      os: { name: osName, version: osVersion },
      device: {
        type: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
        model: isMobile ? 'Mobile Device' : undefined,
      },
    };
  };

  const getDeviceIcon = (userAgent: string) => {
    const parsed = parseUserAgent(userAgent);
    let icon = <Desktop className='' />;
    if (parsed?.device.type === 'mobile') {
      icon = <MobileSolid className='' />;
    } else if (parsed?.device.type === 'tablet') {
      icon = <TabletSolid className='' />;
    }
    return (
      <div className='p-2 bg-gray-100 dark:bg-stone-900 rounded-lg flex items-center justify-center text-slate-900 dark:text-slate-200 [&>svg]:size-6'>
        {icon}
      </div>
    );
  };

  const getDeviceInfo = (userAgent: string) => {
    const parsed = parseUserAgent(userAgent);
    if (!parsed) return 'Unknown Device';

    const { browser, os, device } = parsed;
    const parts = [];

    if (browser.name) {
      parts.push(browser.name);
      if (browser.version) {
        parts.push(browser.version);
      }
    }

    if (os.name) {
      parts.push(`on ${os.name}`);
      if (os.version) {
        parts.push(os.version);
      }
    }

    if (device.model) {
      parts.push(`(${device.model})`);
    }

    return parts.join(' ') || 'Unknown Device';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge
            variant='default'
            className='bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 pointer-events-none'>
            Active
          </Badge>
        );
      case 'REVOKED':
        return <Badge variant='secondary'>Revoked</Badge>;
      case 'EXPIRED':
        return <Badge variant='outline'>Expired</Badge>;
      default:
        return <Badge variant='outline'>{status}</Badge>;
    }
  };

  const getLoginMethodBadge = (method: string) => {
    switch (method) {
      case 'PASSWORD':
        return <Badge variant='outline'>Password</Badge>;
      case 'OTP':
        return <Badge variant='outline'>OTP</Badge>;
      default:
        return <Badge variant='outline'>{method}</Badge>;
    }
  };

  const isCurrentSession = (sId: string) => {
    return sessionId === sId;
  };

  const allSessions = data?.userSessions || [];

  const { activeSessions, expiredSessions } = useMemo(() => {
    const active: typeof allSessions = [];
    const expired: typeof allSessions = [];

    for (const session of allSessions) {
      if (session.status === 'ACTIVE') {
        active.push(session);
      } else {
        expired.push(session);
      }
    }

    // Sort: current session first, then by lastUsedAt descending
    active.sort((a, b) => {
      if (isCurrentSession(a.id)) return -1;
      if (isCurrentSession(b.id)) return 1;
      return dayjs(b.lastUsedAt).valueOf() - dayjs(a.lastUsedAt).valueOf();
    });

    // Sort expired by expiresAt descending (most recent first)
    expired.sort((a, b) => dayjs(b.expiresAt).valueOf() - dayjs(a.expiresAt).valueOf());

    return { activeSessions: active, expiredSessions: expired };
  }, [allSessions, sessionId]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Login Sessions</CardTitle>
          <CardDescription>Manage your active login sessions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='text-center text-sm text-slate-700 py-4'>Loading sessions...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Login Sessions</CardTitle>
          <CardDescription>Manage your active login sessions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='text-center py-4 text-red-600'>Error loading sessions</div>
        </CardContent>
      </Card>
    );
  }

  const renderSession = (session: MeUserSessionsQuery['userSessions'][number]) => (
    <div
      key={session.id}
      className={`flex items-center justify-between gap-4 p-3 rounded-lg ${
        isCurrentSession(session.id) ? 'bg-slate-50 dark:bg-stone-800/50 ring-1 ring-slate-200 dark:ring-stone-700' : ''
      }`}>
      {getDeviceIcon(session.userAgent || '')}
      <div className='flex flex-col flex-1 gap-0.5'>
        <div className='flex items-center gap-1.5'>
          {isCurrentSession(session.id) && (
            <Badge
              variant='default'
              className='bg-slate-900 dark:bg-white dark:text-stone-900 text-white pointer-events-none'>
              Current Session
            </Badge>
          )}
          {getStatusBadge(session.status)}
          {getLoginMethodBadge(session.loginMethod)}
        </div>
        <div className='font-medium text-slate-900 dark:text-white'>
          {session.country ? `${session.country} · ` : ''}
          {getDeviceInfo(session.userAgent || '')}
        </div>
        <div className='flex items-center gap-2 text-xs text-slate-600 dark:text-white/70'>
          <PortalTooltip delayShow={120} id={`login-session-created-at-${session.id}`}>
            Session started: {formatDate(session.createdAt)}
          </PortalTooltip>
          <PortalTooltip delayShow={120} id={`login-session-last-used-at-${session.id}`}>
            Last used: {formatDate(session.lastUsedAt)}
          </PortalTooltip>
          <PortalTooltip delayShow={120} id={`login-session-expires-at-${session.id}`}>
            {session.status === 'ACTIVE' ? 'Expires' : 'Expired'}: {formatDate(session.expiresAt)}
          </PortalTooltip>
          <div data-tooltip-id={`login-session-created-at-${session.id}`}>{dayjs(session.createdAt).fromNow()}</div>
          &middot;
          <div data-tooltip-id={`login-session-last-used-at-${session.id}`}>{dayjs(session.lastUsedAt).fromNow()}</div>
          {session.status !== 'ACTIVE' && (
            <>
              &middot;
              <div data-tooltip-id={`login-session-expires-at-${session.id}`}>{formatDate(session.expiresAt)}</div>
            </>
          )}
        </div>
      </div>
      <div>
        {session.status === 'ACTIVE' && !isCurrentSession(session.id) && (
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleRevokeSession(session.id)}
            disabled={revokingSession === session.id}>
            <Trash className='h-4 w-4 mr-1' />
            {revokingSession === session.id ? 'Revoking...' : 'Revoke'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Login Sessions</CardTitle>
        <CardDescription>
          Manage your active login sessions. You can revoke sessions you no longer recognize.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {allSessions.length === 0 ? (
          <div className='text-center py-4 text-gray-500'>No sessions found</div>
        ) : (
          <div className='flex flex-col gap-2 select-none'>
            {activeSessions.map(renderSession)}

            {expiredSessions.length > 0 && (
              <>
                <button
                  onClick={() => setShowExpired(!showExpired)}
                  className='flex items-center gap-2 text-sm text-slate-500 dark:text-stone-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors py-2 mt-1'>
                  <ChevronDown className={`size-4 transition-transform ${showExpired ? 'rotate-180' : ''}`} />
                  {showExpired ? 'Hide' : 'Show'} expired sessions ({expiredSessions.length})
                </button>

                {showExpired && (
                  <div className='flex flex-col gap-2 opacity-60'>{expiredSessions.map(renderSession)}</div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
